/**
 * FASE 23.8 / 24.8 — RUNTIME RECOVERY + TRUST-PRESERVING FALLBACKS
 *
 * Graceful recovery for corrupt or invalid runtime/workflow state.
 * Never throws, never breaks the UI. Returns safe fallbacks.
 * When recovering, maintains pastoral tone and session coherence.
 */

import { WORKFLOW_STATUS, STEP_STATUS, PRIORITY } from "../kevia-workflows/workflowSchemas";

/* ─── Snapshot recovery ──────────────────────────────────────── */

const KNOWN_MODULES = [
  "inicio", "familias", "personas", "visitas", "oraciones", "eventos",
  "iglesias", "notas", "predicaciones", "materiales", "kevia",
  "operaciones", "auditoría", "usuarios", "revisión", "junta directiva",
];
const KNOWN_MODES   = ["visiting", "prayer", "administration", "general"];

/**
 * Sanitize a restored runtime snapshot, filling in safe defaults
 * for any missing or invalid fields.
 * @param {object|null} snapshot
 * @returns {object} — always safe to spread into runtime state
 */
export function recoverSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return _safeDefaultSnapshot();
  }

  return {
    currentModule:   _safeModule(snapshot.currentModule),
    currentRoute:    typeof snapshot.currentRoute === "string"   ? snapshot.currentRoute  : null,
    activeEntity:    _safeEntity(snapshot.activeEntity),
    lastAction:      typeof snapshot.lastAction   === "string"   ? snapshot.lastAction    : null,
    recentModules:   _safeModuleList(snapshot.recentModules),
    operationalMode: _safeMode(snapshot.operationalMode),
    recentSignals:   _safeSignals(snapshot.recentSignals),
    workflowSummary: _safeWorkflowSummary(snapshot.workflowSummary),
  };
}

/* ─── Workflow recovery ──────────────────────────────────────── */

const VALID_STATUSES = new Set(Object.values(WORKFLOW_STATUS));
const VALID_STEPS    = new Set(Object.values(STEP_STATUS));
const VALID_PRIORITY = new Set(Object.values(PRIORITY));

/**
 * Filter and sanitize a workflow array, removing any invalid entries.
 * @param {any[]} workflows
 * @returns {import("../kevia-workflows/workflowSchemas").Workflow[]}
 */
export function recoverWorkflows(workflows) {
  if (!Array.isArray(workflows)) return [];

  return workflows
    .filter((wf) => wf && typeof wf === "object" && typeof wf.id === "string" && typeof wf.type === "string")
    .map((wf) => ({
      id:                      wf.id,
      type:                    wf.type,
      status:                  VALID_STATUSES.has(wf.status)   ? wf.status   : WORKFLOW_STATUS.ACTIVE,
      priority:                VALID_PRIORITY.has(wf.priority) ? wf.priority : PRIORITY.MEDIUM,
      created_at:              typeof wf.created_at   === "number" ? wf.created_at   : Date.now(),
      updated_at:              typeof wf.updated_at   === "number" ? wf.updated_at   : Date.now(),
      last_activity:           typeof wf.last_activity === "number" ? wf.last_activity : Date.now(),
      progress:                typeof wf.progress === "number" && !isNaN(wf.progress) ? Math.max(0, Math.min(1, wf.progress)) : 0,
      related_entities:        _safeRelatedEntities(wf.related_entities),
      steps:                   _safeSteps(wf.steps),
      next_recommended_action: typeof wf.next_recommended_action === "string" ? wf.next_recommended_action : null,
    }));
}

/**
 * Recover a failed mutation state — resets it to null so the UI
 * doesn't get stuck in a loading/pending state.
 * @param {object|null} pendingAction
 * @returns {object|null}
 */
export function recoverMutationState(pendingAction) {
  if (!pendingAction) return null;
  if (typeof pendingAction.toolId !== "string") return null;
  if (typeof pendingAction.messageId !== "string") return null;
  return pendingAction;
}

/**
 * Detect if a context string is stale (empty, null, or contains only
 * placeholder markers).
 */
export function isStaleContext(str) {
  if (!str || typeof str !== "string") return true;
  const trimmed = str.trim();
  return trimmed.length === 0 || trimmed === "[data: ]" || trimmed === "[runtime: ]";
}

/* ─── Trust-preserving recovery (24.8) ──────────────────────── */

/**
 * Returns a trust-preserving fallback message for a given failure mode.
 * Always pastoral, never technical, never alarmist.
 * @param {"stream"|"mutation"|"snapshot"|"context"|"general"} failureKind
 * @returns {string}
 */
export function getRecoveryMessage(failureKind = "general") {
  const MESSAGES = {
    stream:   "Algo no llegó bien. Puedes intentarlo de nuevo cuando quieras.",
    mutation: "No pude completar esa acción. ¿Quieres intentarlo nuevamente?",
    snapshot: "Estoy empezando con contexto fresco. Puedes continuar normalmente.",
    context:  "No cuento con toda la información de sesión anterior, pero estoy listo para seguir.",
    general:  "Algo no salió bien de mi parte. Inténtalo de nuevo cuando puedas.",
  };
  return MESSAGES[failureKind] ?? MESSAGES.general;
}

/**
 * Returns a partial-state preservation object.
 * When full recovery fails, preserve what we can.
 * @param {object} brokenState
 * @returns {object}
 */
export function preservePartialState(brokenState) {
  if (!brokenState || typeof brokenState !== "object") return {};
  return {
    // Preserve only the fields we're confident about
    ...(typeof brokenState.currentModule === "string" && { currentModule: brokenState.currentModule }),
    ...(typeof brokenState.currentRoute  === "string" && { currentRoute:  brokenState.currentRoute }),
    ...(brokenState.activeEntity?.id     && { activeEntity:  brokenState.activeEntity }),
  };
}

/* ─── Private helpers ────────────────────────────────────────── */

function _safeDefaultSnapshot() {
  return {
    currentModule:   "inicio",
    currentRoute:    null,
    activeEntity:    null,
    lastAction:      null,
    recentModules:   [],
    operationalMode: "general",
    recentSignals:   [],
    workflowSummary: null,
  };
}

function _safeModule(m) {
  return typeof m === "string" && KNOWN_MODULES.includes(m) ? m : "inicio";
}

function _safeMode(m) {
  return typeof m === "string" && KNOWN_MODES.includes(m) ? m : "general";
}

function _safeModuleList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.filter((m) => typeof m === "string" && KNOWN_MODULES.includes(m)).slice(0, 5);
}

function _safeEntity(e) {
  if (!e || typeof e !== "object") return null;
  if (typeof e.id !== "string" || typeof e.type !== "string") return null;
  return { id: e.id, type: e.type, name: typeof e.name === "string" ? e.name : "" };
}

function _safeSignals(signals) {
  if (!Array.isArray(signals)) return [];
  return signals
    .filter((s) => s && typeof s.id === "string")
    .map((s) => ({
      id:         s.id,
      score:      typeof s.score === "number" ? s.score : 0,
      confidence: typeof s.confidence === "number" ? s.confidence : 0,
    }))
    .slice(0, 5);
}

function _safeWorkflowSummary(ws) {
  if (!ws || typeof ws !== "object") return null;
  return {
    count:    typeof ws.count === "number"    ? ws.count    : 0,
    critical: typeof ws.critical === "number" ? ws.critical : 0,
    high:     typeof ws.high === "number"     ? ws.high     : 0,
    types:    Array.isArray(ws.types)         ? ws.types.filter((t) => typeof t === "string") : [],
  };
}

function _safeRelatedEntities(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((e) => e && typeof e.id === "string" && typeof e.type === "string")
    .slice(0, 3);
}

function _safeSteps(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((s) => s && typeof s.id === "string")
    .map((s) => ({
      id:           s.id,
      label:        typeof s.label === "string" ? s.label : s.id,
      status:       VALID_STEPS.has(s.status) ? s.status : STEP_STATUS.PENDING,
      completed_at: typeof s.completed_at === "number" ? s.completed_at : null,
    }));
}
